import { FC, useContext, useMemo } from 'react';

import { useQuery } from '@apollo/client';
import { Table } from '@smartshell/ui-kit';
import { useLocation } from 'react-router-dom';

import { LocalizationContext } from 'localization';
import { Combo, EPaymentItemType, TShopItem } from 'types';

import { ICombosRequest, ICombosResponse } from './interfaces';
import { COMBOS } from './schema';
import s from './ShopCombosTable.module.scss';
import { useColumns } from './useColumns';

interface ICombosFieldProps {
  setCombo: (combo: Combo | null) => void;
  onItemSelect: (item: TShopItem) => void;
  hostId?: number;
  shift?: number;
  search?: string;
  disabledCombo?: boolean;
  clientUUID?: string | null;
  promoCode?: boolean;
}

export const ShopCombosTable: FC<ICombosFieldProps> = ({
  onItemSelect,
  setCombo,
  hostId,
  shift,
  search,
  disabledCombo,
  clientUUID,
  promoCode,
}) => {
  const { t } = useContext(LocalizationContext);

  const location = useLocation();
  const COLUMNS = useColumns();

  const { data, loading } = useQuery<ICombosResponse, ICombosRequest>(COMBOS, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      input: {
        host_id: hostId,
        client_uuid: clientUUID ?? undefined,
        ...(shift ? { shift: shift <= 0 ? 1 : shift } : {}),
      },
    },
  });

  const filteredData = useMemo(
    () =>
      data?.comboList.filter((combo) => {
        if (search && !combo.title.toLowerCase().includes(search.toLowerCase()))
          return false;

        if (location.pathname.includes('/shop') && combo.tariff !== null)
          return false;

        if (
          combo?.items?.some(
            (item) =>
              item.entity_type === 'GOOD' &&
              'available_to_buy' in item.entity &&
              item.entity.available_to_buy === false
          )
        ) {
          return false;
        }
        return combo.quantity;
      }) ?? [],
    [search, data]
  );

  return (
    <Table
      disabled={disabledCombo || promoCode}
      className={s['tariffs-table']}
      data={filteredData}
      columns={COLUMNS}
      onRowClick={async (_, row) => {
        const { cost, id, items, title, tariff, use_global_discounts } =
          row.original;

        setCombo({ id, title, cost, use_global_discounts, tariff, items });

        if (tariff) {
          onItemSelect({
            comboId: id,
            id: tariff.id,
            cost: tariff.cost,
            use_global_discounts: false,
            amount: 1,
            type: EPaymentItemType.TARIFF,
            original: tariff,
          });
        }

        for (const item of items) {
          // @ts-ignore it's ok
          onItemSelect({
            comboId: id,
            id: item.entity.id,
            cost: item.price,
            use_global_discounts: false,
            amount: item.amount,
            type: item.entity_type,
            original: item.entity,
          });
        }
      }}
      isLoading={loading}
      emptyDataPlaceholder={t('shopForm.empty.combos')}
    />
  );
};
